/**
 * Типы для визуализации Git-графа
 */

import type { CommitNode } from '@/types/git';

/** Позиция узла на графе */
export interface NodePosition {
  x: number;
  y: number;
}

/** Цветовая схема для веток */
export type BranchColor =
  | 'main' // Оранжевый (Git brand color)
  | 'feature' // Синий
  | 'develop' // Фиолетовый
  | 'hotfix' // Жёлтый
  | 'release' // Зелёный
  | 'custom'; // Пользовательский цвет

/** Информация о ветке для визуализации */
export interface BranchInfo {
  name: string;
  color: BranchColor;
  hexColor: string;
  isRemote: boolean;
}

/** Узел коммита для визуализации */
export interface CommitNodeVisual {
  commit: CommitNode;
  position: NodePosition;
  branchInfo: BranchInfo | null;
  isHead: boolean;
  isMerge: boolean;
  branchLabels: string[];
}

/** Ребро между коммитами */
export interface CommitEdge {
  fromSha: string;
  toSha: string;
  fromPosition: NodePosition;
  toPosition: NodePosition;
  color: string;
  isMerge: boolean;
}

/** Состояние графа */
export interface GraphState {
  nodes: Map<string, CommitNodeVisual>;
  edges: CommitEdge[];
  branches: Map<string, BranchInfo>;
  headSha: string | null;
  currentBranch: string | null;
}

/** Конфигурация графа */
export interface GraphConfig {
  /** Ширина SVG контейнера */
  width: number;
  /** Высота SVG контейнера */
  height: number;
  /** Отступ сверху */
  paddingTop: number;
  /** Отступ слева */
  paddingLeft: number;
  /** Расстояние между узлами по вертикали */
  nodeSpacingY: number;
  /** Расстояние между ветками по горизонтали */
  branchSpacingX: number;
  /** Радиус узла коммита */
  nodeRadius: number;
  /** Радиус merge-коммита (больше обычного) */
  mergeNodeRadius: number;
  /** Ширина линии связи */
  edgeWidth: number;
  /** Анимация включена */
  animated: boolean;
}

/** Цвета веток по умолчанию */
export const BRANCH_COLORS: Record<BranchColor, string> = {
  main: '#F05032', // Git orange
  feature: '#4a9eff', // Синий
  develop: '#8b5cf6', // Фиолетовый
  hotfix: '#fbbf24', // Жёлтый
  release: '#34d399', // Зелёный
  custom: '#6b7280', // Серый
};

/** Конфигурация по умолчанию */
export const DEFAULT_GRAPH_CONFIG: GraphConfig = {
  width: 400,
  height: 600,
  paddingTop: 40,
  paddingLeft: 60,
  nodeSpacingY: 60,
  branchSpacingX: 80,
  nodeRadius: 12,
  mergeNodeRadius: 16,
  edgeWidth: 2,
  animated: true,
};

/** Состояние подсветки для интеграции со сценариями */
export interface HighlightState {
  /** Тип элемента */
  type: 'commit' | 'branch' | 'head' | 'file' | 'area';
  /** ID элемента */
  id: string;
  /** Цвет подсветки */
  color: string;
  /** Анимация */
  animation: 'pulse' | 'glow' | 'bounce' | 'draw' | undefined;
  /** Описание */
  description: string | undefined;
}
